import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, FileDown, AlertTriangle, Package, Plus, RefreshCw } from 'lucide-react';
import { inventoryTemplatesAPI } from '../lib/api';
import { toast } from 'sonner';

export const ApplyTemplateModal = ({ open, onClose, vehicleId, vehicleName }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templatePreview, setTemplatePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mode, setMode] = useState('add_missing');
  const [confirmStep, setConfirmStep] = useState(false);
  const [applying, setApplying] = useState(false);

  // Fetch templates on open
  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelectedTemplateId('');
      setTemplatePreview(null);
      setMode('add_missing');
      setConfirmStep(false);
    }
  }, [open]);

  // Fetch preview when template selected
  useEffect(() => {
    if (selectedTemplateId) {
      fetchPreview(selectedTemplateId);
    } else {
      setTemplatePreview(null);
    }
  }, [selectedTemplateId]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await inventoryTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (id) => {
    setPreviewLoading(true);
    try {
      const res = await inventoryTemplatesAPI.get(id);
      setTemplatePreview(res.data);
    } catch {
      toast.error('Failed to load template preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }

    setApplying(true);
    try {
      const res = await inventoryTemplatesAPI.apply(selectedTemplateId, {
        vehicle_id: vehicleId,
        mode,
      });
      const data = res.data;
      if (mode === 'add_missing') {
        toast.success(`Added ${data.items_added} items to ${vehicleName}${data.items_skipped > 0 ? ` (${data.items_skipped} already existed)` : ''}`);
      } else {
        toast.success(`Replaced inventory on ${vehicleName} with ${data.items_added} items from template`);
      }
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      onClose(false);
    }
  };

  // Reset confirm step when mode or template changes
  useEffect(() => {
    setConfirmStep(false);
  }, [mode, selectedTemplateId]);

  const confirmMessage = mode === 'add_missing'
    ? `This will add ${templatePreview?.items?.length || 0} parts to ${vehicleName}. Existing items will not be modified.`
    : `This will replace ALL current inventory on ${vehicleName} with ${templatePreview?.items?.length || 0} items from this template.`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="apply-template-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileDown className="w-5 h-5" />
            Apply Inventory Template
          </DialogTitle>
          <DialogDescription>
            Apply a saved inventory template to <strong>{vehicleName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Template Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Select Template *</Label>
            {loading ? (
              <div className="flex items-center gap-2 py-3 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading templates...
              </div>
            ) : templates.length === 0 ? (
              <div className="text-sm text-muted-foreground py-3 text-center bg-muted/50 rounded-lg">
                No templates saved yet. Save a vehicle's inventory as a template first.
              </div>
            ) : (
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger className="h-11" data-testid="template-select">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.item_count} items)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Template Preview */}
          {selectedTemplateId && (
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Preview</Label>
              {previewLoading ? (
                <div className="flex items-center gap-2 py-4 text-muted-foreground justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading preview...
                </div>
              ) : templatePreview ? (
                <div className="border rounded-lg max-h-48 overflow-y-auto" data-testid="template-preview">
                  {templatePreview.description && (
                    <div className="px-3 py-2 bg-muted/50 text-sm text-muted-foreground border-b">
                      {templatePreview.description}
                    </div>
                  )}
                  <div className="divide-y divide-border">
                    {templatePreview.items?.map((item) => (
                      <div key={item.item_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <span className="font-medium truncate block">{item.item_name}</span>
                          {item.item_sku && (
                            <span className="text-xs text-muted-foreground font-mono">{item.item_sku}</span>
                          )}
                        </div>
                        <span className="font-mono font-bold ml-3 shrink-0">{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Apply Mode */}
          {selectedTemplateId && templatePreview && (
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-wide">Apply Mode *</Label>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    mode === 'add_missing' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setMode('add_missing')}
                  data-testid="mode-add-missing"
                >
                  <Plus className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Add missing items only</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Only adds items from the template that are not already on the vehicle. Existing quantities are not modified.</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    mode === 'replace' ? 'border-destructive bg-destructive/5' : 'border-border hover:border-destructive/50'
                  }`}
                  onClick={() => setMode('replace')}
                  data-testid="mode-replace"
                >
                  <RefreshCw className="w-5 h-5 mt-0.5 shrink-0 text-destructive" />
                  <div>
                    <p className="font-semibold text-sm">Replace current inventory</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Removes all current inventory and replaces it with the template contents.</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Confirmation */}
          {confirmStep && (
            <div className={`p-3 rounded-lg border ${mode === 'replace' ? 'bg-destructive/5 border-destructive/30' : 'bg-primary/5 border-primary/30'}`} data-testid="apply-confirmation">
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${mode === 'replace' ? 'text-destructive' : 'text-primary'}`} />
                <p className="text-sm font-medium">{confirmMessage}</p>
              </div>
            </div>
          )}

          {/* Apply Button */}
          {selectedTemplateId && templatePreview && (
            <Button
              className={`w-full h-11 font-bold ${confirmStep && mode === 'replace' ? 'bg-destructive hover:bg-destructive/90' : ''}`}
              onClick={handleApply}
              disabled={applying || !selectedTemplateId}
              data-testid="apply-template-btn"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : confirmStep ? (
                <AlertTriangle className="w-4 h-4 mr-2" />
              ) : (
                <FileDown className="w-4 h-4 mr-2" />
              )}
              {confirmStep ? 'Confirm & Apply' : 'Apply Template'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
